import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Title from "./Title";
import CsvDownloadButton from "react-json-to-csv";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";

import Tooltip from "@mui/material/Tooltip";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import DashboardTable from "./DashboardTable";
import { fetchRooms } from "../../apis/RoomAPI";
import { fetchLocations } from "../../apis/LocationAPI";
import AddRoom from "./AddRoom";
import EditRoom from "./EditRoom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Pagination from "@mui/material/Pagination";

/**
 * Renders the Rooms management component with filtering and pagination
 *
 * Displays a filterable table of rooms with location details and provides
 * options to download data as CSV and add new rooms
 *
 * @returns {JSX.Element} - The rendered Rooms component
 */
function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshError, setRefreshError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchRooms();
        const locationData = await fetchLocations();
        setRooms(data);
        setFilteredRooms(data);
        setLocations(locationData);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Handle error, show error message, etc.
      }
    };
    fetchData();
  }, []);

  // Apply filters whenever relevant state changes
  useEffect(() => {
    let result = [...rooms];

    // Filter by location
    if (locationFilter !== "all") {
      result = result.filter((room) => room.lid === parseInt(locationFilter));
    }

    // Filter by search term
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(
        (room) =>
          room.name.toLowerCase().includes(lowercasedSearch) ||
          room.room_id.toString().includes(lowercasedSearch)
      );
    }

    setFilteredRooms(result);

    // Calculate total pages
    const pageCount = Math.max(1, Math.ceil(result.length / rowsPerPage));
    setTotalPages(pageCount);
    setPage((currentPage) => Math.min(currentPage, pageCount));
  }, [rooms, locationFilter, searchTerm, rowsPerPage]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1); // Reset to first page when changing rows per page
  };

  const handleLocationFilterChange = (event) => {
    setLocationFilter(event.target.value);
    setPage(1); // Reset to first page when changing filter
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when changing search
  };

  // Pagination logic
  const paginatedRooms = filteredRooms.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Table columns
  const columns = ["Room ID", "Name", "Location", "Capacity", "Hourly Cost"];

  const columnMapping = {
    "Room ID": "room_id",
    Name: "name",
    Location: "lid",
    Capacity: "cap",
    "Hourly Cost": "credits_hourly",
  };

  // Function to retrieve location name based on lid
  const getLocationName = (lid) => {
    const location = locations.find((location) => location.lid === lid);
    return location ? location.name : "N/A";
  };

  const formatValues = {
    Location: getLocationName,
  };

  const handleRoomsChanged = async () => {
    setRefreshError("");
    try {
      const data = await fetchRooms();
      setRooms(data);
    } catch (error) {
      setRefreshError("Room saved, but the list could not be refreshed. Reload to see the latest rooms.");
    }
  };

  const handleExportQRCodes = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    
    // Load local logo image
    const loadImage = (src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = src;
            img.onload = () => resolve(img);
            img.onerror = reject;
        });
    };

    let logoImg = null;
    try {
        logoImg = await loadImage(`${window.location.origin}/logo512.png`);
    } catch (err) {
        console.warn("Could not load logo image", err);
    }
    
    // Iterate over filteredRooms
    for (let i = 0; i < filteredRooms.length; i++) {
        if (i > 0) {
            doc.addPage();
        }

        const room = filteredRooms[i];
        const locationName = getLocationName(room.lid);
        
        const url = `${window.location.origin}/rooms?location=${room.lid}&room=${room.room_id}`;
        
        try {
            // Generate high-quality QR code
            const qrDataUrl = await QRCode.toDataURL(url, { width: 512, margin: 2 });
            
            // Add Logo
            if (logoImg) {
                const logoSize = 40;
                doc.addImage(logoImg, 'PNG', (pageWidth - logoSize) / 2, 20, logoSize, logoSize);
            }

            // Room Name
            doc.setFontSize(24);
            doc.setFont("helvetica", "bold");
            doc.text(`${room.name}`, pageWidth / 2, 80, { align: "center" });
            
            // Location Name
            doc.setFontSize(16);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100);
            doc.text(`${locationName}`, pageWidth / 2, 90, { align: "center" });
            doc.setTextColor(0); // Reset color

            // QR Code
            const qrSize = 100;
            doc.addImage(qrDataUrl, 'PNG', (pageWidth - qrSize) / 2, 110, qrSize, qrSize);
            
            // Footer/Instructions
            doc.setFontSize(12);
            doc.setTextColor(150);
            doc.text("Scan to view room availability and book", pageWidth / 2, 220, { align: "center" });
            doc.setTextColor(0);

        } catch (err) {
            console.error("Error generating QR for room", room.name, err);
        }
    }
    
    doc.save("Room_QR_Codes.pdf");
  };

  return (
    <Grid
      container
      spacing={3}
    >
      {/* Rooms Management Section */}
      <Grid
        item
        xs={12}
      >
        <Paper
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <React.Fragment>
            <Title>
              Rooms
              <CsvDownloadButton
                data={filteredRooms}
                filename={"Room List.csv"}
                style={{
                  background: "none",
                  border: "none",
                  padding: "0px 0px 0px 10px",
                  float: "right",
                  cursor: "pointer",
                }}
              >
                <Tooltip title="Export to CSV">
                  <FileDownloadOutlinedIcon />
                </Tooltip>
              </CsvDownloadButton>

              <Tooltip title="Export QR Codes">
                <QrCode2Icon 
                  onClick={handleExportQRCodes}
                  sx={{ 
                    cursor: "pointer", 
                    float: "right", 
                    ml: 1,
                    color: "black" // Match other icons
                  }}
                />
              </Tooltip>
              <AddRoom
                onRoomAdded={handleRoomsChanged}
                locationsList={locations}
              />
              <EditRoom data={rooms} locationsList={locations} onRoomEdited={handleRoomsChanged} />
            </Title>

            {refreshError && <Alert severity="warning">{refreshError}</Alert>}

            {/* Filters */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
                mt: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel id='location-filter-label'>
                    Filter by Location
                  </InputLabel>
                  <Select
                    labelId='location-filter-label'
                    id='location-filter'
                    value={locationFilter}
                    label='Filter by Location'
                    onChange={handleLocationFilterChange}
                  >
                    <MenuItem value='all'>All Locations</MenuItem>
                    {locations.map((location) => (
                      <MenuItem
                        key={location.lid}
                        value={location.lid}
                      >
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label='Search'
                  variant='outlined'
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder='Search by name or ID'
                  size='small'
                />
              </Box>

              <FormControl sx={{ width: 120 }}>
                <InputLabel id='rows-per-page-label'>Rows per page</InputLabel>
                <Select
                  labelId='rows-per-page-label'
                  id='rows-per-page'
                  value={rowsPerPage}
                  label='Rows per page'
                  onChange={handleRowsPerPageChange}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <DashboardTable
              data={paginatedRooms}
              columns={columns}
              columnMapping={columnMapping}
              refreshData={fetchRooms}
              formatValues={formatValues}
            />

            {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color='primary'
              />
            </Box>
          </React.Fragment>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default Rooms;
